import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DateUtilityService {
  /**
   * Parse API date string to Date object
   */
  parseAPIDate(dateString: string): Date | null {
    if (!dateString) return null;

    try {
      // Handle ISO format (e.g., "2025-06-10T00:00:00Z")
      if (dateString.includes('T')) {
        const isoDate = new Date(dateString);
        // Create a new date using just the date components to avoid timezone issues
        return new Date(
          isoDate.getUTCFullYear(),
          isoDate.getUTCMonth(),
          isoDate.getUTCDate()
        );
      }

      // Handle MM-DD-YYYY format
      if (dateString.includes('-') && dateString.length === 10) {
        const [month, day, year] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
      }

      // Fallback: try direct parsing but convert to local date components
      const parsedDate = new Date(dateString);
      return new Date(
        parsedDate.getFullYear(),
        parsedDate.getMonth(),
        parsedDate.getDate()
      );
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return null;
    }
  }

  /**
   * Format date for API submission (MM-DD-YYYY) - timezone safe
   * @param date - JavaScript Date object
   * @returns Formatted date string
   */
  formatDateForAPI(date: Date): string {
    // Use local date components to avoid timezone conversion issues
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }

  /**
   * Format ISO date for comparison with generated dates
   * @param isoDate - ISO date string
   * @returns Formatted date string for comparison
   */
  formatISODateForComparison(isoDate: string): string {
    if (!isoDate) return '';

    try {
      // Use the same parsing logic as parseAPIDate to ensure consistency
      const parsedDate = this.parseAPIDate(isoDate);
      if (!parsedDate) return '';

      // Use formatDateForAPI to ensure the same format
      return this.formatDateForAPI(parsedDate);
    } catch (error) {
      console.error(
        'Error formatting ISO date for comparison:',
        isoDate,
        error
      );
      return '';
    }
  }

  /**
   * Check if a date is current or in the past (non-editable)
   */
  isDateCurrentOrInPast(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate <= today;
  }

  /**
   * Get minimum end date (today)
   */
  getMinEndDate(): Date {
    return new Date();
  }

  /**
   * Get current date with time set to midnight
   */
  getTodayAtMidnight(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  /**
   * Compare two dates (ignoring time)
   */
  isSameDate(date1: Date, date2: Date): boolean {
    if (!date1 || !date2) return false;

    const d1 = new Date(date1);
    const d2 = new Date(date2);

    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);

    return d1.getTime() === d2.getTime();
  }

  /**
   * Get day name from date
   * @param date - JavaScript Date object
   * @returns Day name (e.g., "Monday")
   */
  getDayName(date: Date): string {
    const options: Intl.DateTimeFormatOptions = { weekday: 'long' };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Format date for display purposes
   * @param date - JavaScript Date object
   * @returns Human-readable date string
   */
  formatDateForDisplay(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Check if a date is today (current date)
   */
  isToday(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate.getTime() === today.getTime();
  }

  /**
   * Check if a date is strictly in the past (before today)
   */
  isStrictlyInPast(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  }

  /**
   * Check if a date is in the past
   * @param date - Date to check
   * @returns True if date is in the past
   */
  isDateInPast(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for proper comparison

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0); // Set to start of day for proper comparison

    return checkDate <= today;
  }

  /**
   * Parse ISO date string ignoring timezone
   * @param isoDateString - ISO date string
   * @returns Local Date object
   */
  parseISODateIgnoreTimezone(isoDateString: string): Date {
    // Extract just the date part and create a local date without timezone conversion
    // Input format: "2025-06-10T00:00:00Z"
    // Extract: "2025-06-10"
    const datePart = isoDateString.split('T')[0];
    const [year, month, day] = datePart
      .split('-')
      .map((num) => parseInt(num, 10));
    // Create date in local timezone (month is 0-indexed in JavaScript Date constructor)
    return new Date(year, month - 1, day);
  }
}
